import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as blinder from 'color-blind';
import { CONTRAST_ABOUT } from '../../../../data/tool/tool-about.data';
import { NAVIGATIONS } from '../../../../data/navigation/navigation.data';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { ColorService } from '../../../../service/color/color.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { randomThemeContrastPair } from '../../../../util/contrast-pair';

/** A passing pair the Fixes section offers: one of the two colours moved in lightness until it passes. */
interface ContrastFix {
  title: string;
  note: string;
  fg: string;
  bg: string;
  ratio: string;
  /** false when no lightness of that colour reaches the target */
  reachable: boolean;
}

/** The two colours as one colour vision deficiency sees them. */
interface VisionPair {
  name: string;
  note: string;
  fg: string;
  bg: string;
  ratio: string;
  pass: boolean;
}

/**
 * /tools/contrast-checker - the WCAG contrast checker (2026-10-15). A text colour and a background colour in the
 * card (app-contrast-widget) give the WCAG 2 ratio, the AA / AAA checks and the APCA Lc; the page then offers the
 * nearest passing colours (Fixes) and the pair through each colour vision deficiency. ?fg=1a3746&bg=e4ecef opens it
 * on a pair and every change writes the pair back to the url, so a result can be shared. Nothing leaves the browser.
 */
@Component( {
  selector: 'app-contrast-page',
  templateUrl: './contrast-page.component.html',
  styleUrls: [
    './contrast-page.component.scss',
    '../wallpaper-page/wallpaper-page.component.scss',
    '../tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class ContrastPageComponent implements OnInit, AfterViewInit, OnDestroy {

  fg = '#1A3746';
  bg = '#E4ECEF';
  ratio = 1;
  fixes: ContrastFix[] = [];
  vision: VisionPair[] = [];

  pageSections: { key: string; label: string }[] = [
    { key: 'fixes', label: 'Fixes' },
    { key: 'vision', label: 'Color Vision' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'contrast-checker';
  activeSection = 'top';
  readonly aboutLead = CONTRAST_ABOUT.lead;
  readonly aboutParagraphs = CONTRAST_ABOUT.paragraphs;
  readonly faq = CONTRAST_ABOUT.faq;

  private readonly VISION_TYPES: { name: string; type: string; note: string }[] = [
    { name: 'Protanomaly', type: 'protanomaly', note: 'Reds desaturate and darken' },
    { name: 'Protanopia', type: 'protanopia', note: 'Red and green are confused, reds look dim' },
    { name: 'Deuteranomaly', type: 'deuteranomaly', note: 'The most common deficiency' },
    { name: 'Deuteranopia', type: 'deuteranopia', note: 'Red and green are confused' },
    { name: 'Tritanomaly', type: 'tritanomaly', note: 'Blue and green converge' },
    { name: 'Tritanopia', type: 'tritanopia', note: 'Blue and yellow are confused' },
    { name: 'Achromatomaly', type: 'achromatomaly', note: 'Colors heavily desaturated' },
    { name: 'Achromatopsia', type: 'achromatopsia', note: 'Only lightness, seen as grayscale' },
  ];

  private sectionObserver?: IntersectionObserver;
  private subs: Subscription[] = [];
  private loggedChange = false;
  private started = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private colorService: ColorService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.seoService.setMetaData( {
      title: NAVIGATIONS.contrastChecker.title,
      description: NAVIGATIONS.contrastChecker.description,
      keywords: NAVIGATIONS.contrastChecker.keywords,
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
    // ?fg=1a3746&bg=e4ecef opens the checker on that pair
    this.subs.push( this.route.queryParams.subscribe( q => {
      const fg = this.hex( q.fg );
      const bg = this.hex( q.bg );
      if ( fg && bg ) {
        this.fg = fg;
        this.bg = bg;
      } else if ( !this.started ) {
        // no pair in the link: a random Codigrate theme's editor text on its editor background (owner 2026-10-15)
        const pair = randomThemeContrastPair();
        this.fg = fg || pair.fg;
        this.bg = bg || pair.bg;
      }
      this.started = true;
      this.build();
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
          }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
  }

  /** the card changed a colour: the sections follow and the pair goes into the url */
  onColors( pair: { fg: string; bg: string } ): void {
    this.use( pair.fg, pair.bg, 'card' );
  }

  /** a fix taken over into the card */
  use( fg: string, bg: string, source: 'card' | 'fix' ): void {
    this.fg = fg;
    this.bg = bg;
    this.build();
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: { fg: fg.substring( 1 ).toLowerCase(), bg: bg.substring( 1 ).toLowerCase() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    } );
    if ( source === 'fix' || !this.loggedChange ) {
      this.loggedChange = source === 'card' || this.loggedChange;
      this.analyticsService.logEvent( source === 'fix' ? 'Contrast Fix Applied' : 'Contrast Checked', { fg, bg, ratio: this.ratio } );
    }
  }

  get ratioLabel(): string {
    return this.label( this.ratio );
  }

  // ---------------------------------------------------------------- the sections' data

  private build(): void {
    this.ratio = this.colorService.contrastRatio( this.fg, this.bg );
    this.fixes = this.buildFixes();
    this.vision = this.VISION_TYPES.map( v => {
      const fg = this.simulate( v.type, this.fg );
      const bg = this.simulate( v.type, this.bg );
      const ratio = this.colorService.contrastRatio( fg, bg );
      return { name: v.name, note: v.note, fg, bg, ratio: this.label( ratio ), pass: ratio >= 4.5 };
    } );
  }

  /**
   * The nearest passing colours: the text colour, then the background, moved only in lightness (same hue and
   * saturation) to the closest value that reaches AA (4.5:1) and AAA (7:1). A pair that already passes a target
   * is shown as it is.
   */
  private buildFixes(): ContrastFix[] {
    const out: ContrastFix[] = [];
    for ( const target of [ 4.5, 7 ] ) {
      const level = target === 7 ? 'AAA' : 'AA';
      const text = this.nearestPassing( this.fg, this.bg, target );
      out.push( this.fix( `Text for ${ level }`, text, this.fg, text || this.fg, this.bg, target ) );
      const back = this.nearestPassing( this.bg, this.fg, target );
      out.push( this.fix( `Background for ${ level }`, back, this.bg, this.fg, back || this.bg, target ) );
    }
    return out;
  }

  private fix( title: string, found: string | null, original: string, fg: string, bg: string, target: number ): ContrastFix {
    if ( !found ) {
      return { title, note: `No lightness of this hue reaches ${ this.label( target ) }:1 here`, fg, bg, ratio: this.label( this.colorService.contrastRatio( fg, bg ) ), reachable: false };
    }
    return { title, note: found === original ? 'Already passes' : 'Same hue, new lightness', fg, bg, ratio: this.label( this.colorService.contrastRatio( fg, bg ) ), reachable: true };
  }

  /** the colour of the same hue and saturation, nearest in lightness, that reaches the target against `other` */
  private nearestPassing( hex: string, other: string, target: number ): string | null {
    if ( this.colorService.contrastRatio( hex, other ) >= target ) {
      return hex;
    }
    const { h, s, l } = this.colorService.hexToHslInts( hex );
    for ( let d = 1; d <= 100; d++ ) {
      for ( const next of [ l - d, l + d ] ) {
        if ( next < 0 || next > 100 ) {
          continue;
        }
        const candidate = '#' + this.colorService.hslToHex( h, s, next ).toUpperCase();
        if ( this.colorService.contrastRatio( candidate, other ) >= target ) {
          return candidate;
        }
      }
    }
    return null;
  }

  private simulate( type: string, hex: string ): string {
    const fn = ( blinder as any )[ type ];
    const out = typeof fn === 'function' ? String( fn( hex ) ) : hex;
    return this.hex( out ) || hex;
  }

  private hex( raw: string | null | undefined ): string {
    const clean = ( raw || '' ).replace( /[^0-9a-f]/gi, '' );
    return clean.length === 6 ? '#' + clean.toUpperCase() : '';
  }

  private label( ratio: number ): string {
    return ratio.toFixed( 2 ).replace( /\.?0+$/, '' );
  }

  // ---------------------------------------------------------------- the chips

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
